/**
 * Kiosk heartbeat.
 *
 * Each kiosk sends a periodic beat to /api/beat, which relays it to Uptime
 * Kuma. When the beats stop, the machine is off, the network is down, the
 * browser crashed or the JS died, which is exactly the set of cases we care
 * about. A plain ICMP ping would not tell any of them apart from a healthy
 * kiosk displaying a blank page.
 *
 * The module does nothing at all when the `kiosk` URL parameter is missing, so
 * the site stays usable outside a kiosk.
 *
 * No kiosk-specific value lives here. The identity comes from the URL, injected
 * by the Windows launcher, so the bundle is identical on all three kiosks.
 */

// The endpoint is served from the same origin as the page: no CORS, and more
// importantly no preflight request, which does not reliably complete while the
// page is unloading (the pagehide sendBeacon depends on it).
const ENDPOINT_URL = '/api/beat';

const INTERVAL_MS = 120000; // one beat every 120 seconds
const TIMEOUT_MS = 10000;   // give up on the in-flight beat past this point
const SESSION_KEY = 'kiosk-identity';

/**
 * Reads the kiosk identity.
 *
 * Highway, the site router, navigates to "/list.html" via pushState and thereby
 * wipes the query string, so the parameters do not survive the first
 * navigation. We copy them to sessionStorage on first load so the identity
 * survives a full page reload (watchdog restart, crash, F5).
 *
 * Returns null when the kiosk cannot be identified, which disables the module.
 * Also used by the offline support, which only runs on kiosks too.
 */
export function readIdentity() {
  try {
    const parameters = new URLSearchParams(window.location.search);
    const kiosk = (parameters.get('kiosk') || '').trim();

    if (kiosk !== '') {
      const identity = { id: kiosk, uuid: (parameters.get('uuid') || '').trim() };
      try {
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(identity));
      } catch (e) {
        // sessionStorage unavailable: carry on with the URL alone.
      }
      return identity;
    }

    const stored = window.sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      const identity = JSON.parse(stored);
      if (identity && identity.id) {
        return identity;
      }
    }
  } catch (e) {
    // Any failure here means "no identifiable kiosk".
  }
  return null;
}

export default function startHeartbeat() {
  const identity = readIdentity();
  if (!identity) {
    return; // Not a kiosk: no effect, no logging.
  }

  const startedAt = Date.now();
  let lastInteraction = Date.now();
  let errorCount = 0;
  let requestInFlight = false;
  let rttMs = null; // duration of the previous beat, reported as latency

  // Passive: never blocks scrolling or touch responsiveness.
  ['click', 'touchstart', 'keydown'].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      lastInteraction = Date.now();
    }, { passive: true });
  });

  // addEventListener rather than window.onerror: overwrites no existing handler.
  window.addEventListener('error', function () { errorCount += 1; });
  window.addEventListener('unhandledrejection', function () { errorCount += 1; });

  function buildPayload() {
    return {
      id: identity.id,
      uuid: identity.uuid,
      // Highway does not reload the page, so pathname does reflect the current view.
      route: window.location.pathname,
      idleSeconds: Math.round((Date.now() - lastInteraction) / 1000),
      errors: errorCount,
      // Time since the document actually loaded, not since the last navigation:
      // in a single page app this is the browser's uptime.
      uptimeSeconds: Math.round((Date.now() - startedAt) / 1000),
      rttMs: rttMs
    };
  }

  async function send() {
    // One request in flight at a time: a network outage must not pile up
    // pending beats that would all fire at once when the link comes back.
    if (requestInFlight) {
      return;
    }
    requestInFlight = true;

    const requestStart = Date.now();
    const controller = new AbortController();
    const timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);

    try {
      await fetch(ENDPOINT_URL, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
        signal: controller.signal
      });
      rttMs = Date.now() - requestStart;
    } catch (e) {
      rttMs = null; // failed: no latency to report on the next beat
    } finally {
      clearTimeout(timer);
      requestInFlight = false;
    }
  }

  // Final beat before shutdown. sendBeacon survives unloading, a regular fetch
  // does not.
  window.addEventListener('pagehide', function () {
    try {
      const body = new Blob([JSON.stringify(buildPayload())], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT_URL, body);
    } catch (e) {
      // Silence: nothing may surface in the page.
    }
  });

  send(); // immediate first beat, so we learn early that the kiosk is up
  setInterval(send, INTERVAL_MS);
}
